/**
 * Mapbox matching helpers — calculs géométriques pour le covoiturage.
 *
 * Sprint 6 : matching conducteur ↔ passager privacy-first style Karos.
 *
 * Plutôt que d'embarquer turf.js (~80KB gzipped), on implémente directement
 * les 3 calculs dont on a besoin :
 *   - distance d'un point à un segment (point-to-segment, utile pour
 *     mesurer combien un passager se trouve "sur la route" du conducteur)
 *   - point le plus proche sur une polyline (route Mapbox)
 *   - longueur cumulée d'une polyline (pour exprimer l'overlap en km)
 *
 * Toutes les fonctions opèrent sur des coordonnées WGS-84 [lng, lat] et
 * utilisent l'approximation locale plate (suffisante < 100km, on n'est pas
 * en navigation transcontinentale).
 *
 * @see src/lib/mapbox/pickupPoints.ts (utilise ces helpers pour les POI)
 * @see src/hooks/useCarpoolMatching.ts (orchestration React Query)
 */
import { haversineDistanceM, type LngLat } from './client';

export type LngLatTuple = [number, number]; // [lng, lat] — format Mapbox/GeoJSON

/**
 * Distance d'un point P au segment AB, en mètres.
 *
 * Approximation : on projette en plan local équirectangulaire centré sur le
 * point d'intérêt, puis on applique le calcul classique projection-segment.
 * Erreur < 0.5% sur les distances < 10 km, ce qui est largement suffisant
 * pour les seuils covoit (~200m).
 */
export function distancePointToSegmentM(
  point: LngLat,
  a: LngLat,
  b: LngLat,
): number {
  // Projection équirectangulaire locale autour de "point"
  const cosLat = Math.cos((point.lat * Math.PI) / 180);
  const mPerDegLat = 111_320; // approx, varie peu avec la latitude
  const toMeters = (p: LngLat) => ({
    x: (p.lng - point.lng) * mPerDegLat * cosLat,
    y: (p.lat - point.lat) * mPerDegLat,
  });

  const P = { x: 0, y: 0 };
  const A = toMeters(a);
  const B = toMeters(b);

  const dx = B.x - A.x;
  const dy = B.y - A.y;
  const lenSq = dx * dx + dy * dy;

  if (lenSq === 0) {
    // segment dégénéré → distance au point A
    return Math.hypot(P.x - A.x, P.y - A.y);
  }

  // Projection scalaire de P sur AB, clampée à [0,1]
  let t = ((P.x - A.x) * dx + (P.y - A.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  const projX = A.x + t * dx;
  const projY = A.y + t * dy;

  return Math.hypot(P.x - projX, P.y - projY);
}

/**
 * Distance minimale d'un point à une polyline (route Mapbox).
 * Renvoie aussi l'index du segment le plus proche pour pouvoir
 * calculer l'overlap "à partir de tel point sur la route".
 */
export function distancePointToPolylineM(
  point: LngLat,
  polyline: LngLatTuple[],
): { distanceM: number; segmentIndex: number } {
  if (polyline.length < 2) {
    if (polyline.length === 1) {
      return {
        distanceM: haversineDistanceM(point, { lng: polyline[0][0], lat: polyline[0][1] }),
        segmentIndex: 0,
      };
    }
    return { distanceM: Infinity, segmentIndex: -1 };
  }

  let minDist = Infinity;
  let minIdx = 0;
  for (let i = 0; i < polyline.length - 1; i++) {
    const a = { lng: polyline[i][0], lat: polyline[i][1] };
    const b = { lng: polyline[i + 1][0], lat: polyline[i + 1][1] };
    const d = distancePointToSegmentM(point, a, b);
    if (d < minDist) {
      minDist = d;
      minIdx = i;
    }
  }
  return { distanceM: minDist, segmentIndex: minIdx };
}

/**
 * Longueur cumulée (mètres) d'une polyline jusqu'à un index de segment donné
 * (exclusif — s'arrête au début du segment `upToIndex`).
 *
 * Utilisé pour calculer la fraction parcourue par le conducteur avant
 * d'atteindre le pickup point passager → ETA = departure_time + fraction × duration.
 */
export function polylineLengthUpToIndexM(
  polyline: LngLatTuple[],
  upToIndex: number,
): number {
  const end = Math.min(upToIndex, polyline.length - 1);
  let total = 0;
  for (let i = 0; i < end; i++) {
    total += haversineDistanceM(
      { lng: polyline[i][0], lat: polyline[i][1] },
      { lng: polyline[i + 1][0], lat: polyline[i + 1][1] },
    );
  }
  return total;
}

/**
 * Longueur cumulée (mètres) d'une polyline, par sommation haversine.
 * Précis < 0.1% car on additionne des petits segments.
 */
export function polylineLengthM(polyline: LngLatTuple[]): number {
  if (polyline.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < polyline.length - 1; i++) {
    total += haversineDistanceM(
      { lng: polyline[i][0], lat: polyline[i][1] },
      { lng: polyline[i + 1][0], lat: polyline[i + 1][1] },
    );
  }
  return total;
}

/**
 * Score de matching covoit : à quel point le passager est "sur la route"
 * du conducteur ?
 *
 * Heuristique simple :
 *   - On mesure la distance du domicile du passager à la route du conducteur
 *     (plus c'est petit, mieux c'est).
 *   - On en déduit un score 0..1 où :
 *       d ≤ 200m  → score 1 (sur la route)
 *       d ≥ 2km   → score 0 (hors itinéraire)
 *       interpol  → linéaire entre les deux
 *
 * Pourquoi pas turf.lineSlice + nearestPointOnLine ?
 * → Pour V1 on ne calcule pas l'overlap exact (km partagés). On retourne
 * juste un score de "compatibilité d'itinéraire". L'overlap exact viendra
 * en V2 quand on aura besoin de calculer un % de partage de trajet pour
 * la répartition FMD.
 */
export interface MatchingScore {
  /** Distance domicile passager → route conducteur, en mètres. */
  detourM: number;
  /** Score 0..1 (1 = idéal, 0 = pas du tout sur la route). */
  score: number;
  /** Index segment route le plus proche (utile pour pickup points). */
  segmentIndex: number;
}

export function computeMatchingScore(
  passengerHome: LngLat,
  driverRoute: LngLatTuple[],
): MatchingScore {
  const { distanceM, segmentIndex } = distancePointToPolylineM(passengerHome, driverRoute);

  const score = distanceM <= 200
    ? 1
    : distanceM >= 2000
      ? 0
      : 1 - (distanceM - 200) / (2000 - 200);

  return { detourM: distanceM, score, segmentIndex };
}
